import type {
  FundSource,
  FundSourceFilter,
  Store,
} from "schema/gen/es/chiikipoint/model/v2/model_pb";
import type { GetFundSourceFilterMapResponse } from "schema/gen/es/chiikipoint/wallet/v2/service_pb";
import { t } from "./i18n";

export type FundSourceAvailability = {
  fundSource: FundSource;
  isAvailable: boolean;
  unavailableReason: string | null;
};

export type FundSourceFiltersById = Record<string, FundSourceFilter[]>;

const UNAVAILABLE_REASON = t("この店舗では利用できません");

export const normalizeFundSourceFilterMap = (
  filterMap: GetFundSourceFilterMapResponse["filterMap"] | undefined,
): FundSourceFiltersById => {
  if (!filterMap) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(filterMap).map(([fundSourceId, filterList]) => [
      fundSourceId,
      filterList.filters ?? [],
    ]),
  );
};

export const isFundSourceFilterMatched = (
  filter: FundSourceFilter,
  store: Pick<Store, "id" | "city">,
) => {
  if (filter.availableStoreId && filter.availableStoreId !== store.id) {
    return false;
  }

  if (filter.availableCity && filter.availableCity !== store.city) {
    return false;
  }

  return true;
};

export type FundSourceFilterCounts = {
  storeFilterCount: number;
  cityFilterCount: number;
};

/**
 * FundSourceの店舗・地域制限の厳しさを件数化する。
 * フィルタが1件も無い（=制限なしで全店舗利用可）場合は、
 * 他のFundSourceとの優先度比較で「制限が無い＝最も緩い」と判定できるようPositiveInfinityを返す。
 */
export const getFundSourceFilterCounts = (
  fundSourceId: string,
  filtersByFundSourceId: FundSourceFiltersById,
): FundSourceFilterCounts => {
  const filters = filtersByFundSourceId[fundSourceId] ?? [];
  const storeFilterCount = filters.filter((f) => f.availableStoreId).length;
  const cityFilterCount = filters.filter((f) => f.availableCity).length;

  return {
    storeFilterCount:
      storeFilterCount === 0 ? Number.POSITIVE_INFINITY : storeFilterCount,
    cityFilterCount:
      cityFilterCount === 0 ? Number.POSITIVE_INFINITY : cityFilterCount,
  };
};

/**
 * 各要素に、その要素のfundSourceに対応する店舗・地域制限件数を付与する。
 * sortBalanceWithFundSourcesなど優先度計算を行う関数へ渡す前に、
 * 呼び出し元で必ずこの関数を通してから渡す。
 */
export const attachFundSourceFilterCounts = <
  T extends { fundSource?: { id?: string } },
>(
  items: T[],
  filtersByFundSourceId: FundSourceFiltersById,
): (T & FundSourceFilterCounts)[] => {
  return items.map((item) => ({
    ...item,
    ...getFundSourceFilterCounts(
      item.fundSource?.id ?? "",
      filtersByFundSourceId,
    ),
  }));
};

export const isFundSourceAvailableAtStore = ({
  store,
  fundSourceId,
  filtersByFundSourceId,
}: {
  store: Pick<Store, "id" | "city">;
  fundSourceId: string;
  filtersByFundSourceId: FundSourceFiltersById;
}) => {
  const filters = filtersByFundSourceId[fundSourceId] ?? [];

  if (filters.length === 0) {
    return true;
  }

  return filters.some((filter) => isFundSourceFilterMatched(filter, store));
};

export const getFundSourceAvailabilityMapForStore = ({
  store,
  fundSources,
  filtersByFundSourceId,
}: {
  store: Pick<Store, "id" | "city">;
  fundSources: FundSource[];
  filtersByFundSourceId: FundSourceFiltersById;
}) => {
  const seenFundSourceIds = new Set<string>();
  const availabilityByFundSourceId: Record<string, FundSourceAvailability> = {};
  const availableFundSources: FundSource[] = [];

  for (const fundSource of fundSources) {
    const fundSourceId = fundSource.id;
    if (!fundSourceId || seenFundSourceIds.has(fundSourceId)) {
      continue;
    }
    seenFundSourceIds.add(fundSourceId);

    const isAvailable = isFundSourceAvailableAtStore({
      store,
      fundSourceId,
      filtersByFundSourceId,
    });

    availabilityByFundSourceId[fundSourceId] = {
      fundSource,
      isAvailable,
      unavailableReason: isAvailable ? null : UNAVAILABLE_REASON,
    };

    if (isAvailable) {
      availableFundSources.push(fundSource);
    }
  }

  return {
    availabilityByFundSourceId,
    availableFundSources,
  };
};

export const sortItemsByFundSourceAvailability = <
  TItem extends {
    fundSource?: {
      id?: string;
    } | null;
  },
>({
  items,
  availabilityByFundSourceId,
}: {
  items: TItem[];
  availabilityByFundSourceId: Record<
    string,
    {
      isAvailable: boolean;
    }
  >;
}) => {
  const availableItems: TItem[] = [];
  const unavailableItems: TItem[] = [];

  for (const item of items) {
    const fundSourceId = item.fundSource?.id ?? "";
    const isAvailable =
      availabilityByFundSourceId[fundSourceId]?.isAvailable ?? true;

    if (isAvailable) {
      availableItems.push(item);
      continue;
    }

    unavailableItems.push(item);
  }

  return [...availableItems, ...unavailableItems];
};

export const filterStoresBySelectedFundSources = <TStore extends Store>({
  stores,
  selectedFundSourceIds,
  filtersByFundSourceId,
}: {
  stores: TStore[];
  selectedFundSourceIds: string[];
  filtersByFundSourceId: FundSourceFiltersById;
}) => {
  if (selectedFundSourceIds.length === 0) {
    return stores;
  }

  return stores.filter((store) =>
    selectedFundSourceIds.some((fundSourceId) =>
      isFundSourceAvailableAtStore({
        store,
        fundSourceId,
        filtersByFundSourceId,
      }),
    ),
  );
};
